import React, { useState } from 'react';
import { 
  ArrowRight, 
  Linkedin, 
  Github, 
  Twitter, 
  Mail,
  Users,
  Clock,
  Star,
  Trophy,
  Code,
  TrendingUp,
  Shield,
  Rocket,
  Target,
  Zap,
  Heart,
  Globe,
  MessageSquare,
  Award,
  DollarSign
} from 'lucide-react';

interface TeamProps {
  onLaunchPlatform: () => void;
  onNavigateToPage: (page: string) => void;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  fullRole: string;
  description: string;
  skills: string[];
  experience: string;
  heroImage: string;
  profileImage: string;
  accentColor: string;
  icon: React.ReactNode;
  xUsername: string;
}

export default function TeamPage({ onLaunchPlatform, onNavigateToPage }: TeamProps) {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [hoveredMember, setHoveredMember] = useState<number | null>(null);

  // EKKO Logo URL
  const EKKO_LOGO = "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png";

  const teamMembers: TeamMember[] = [
    {
      id: 1,
      name: 'Morgan',
      role: 'Lead Developer',
      fullRole: 'Lead Developer and Founder',
      description: 'Morgan is responsible for overseeing the project\'s overall technical architecture and long term vision. He leads the core development process, ensuring that the platform is secure, scalable, and aligned with the latest industry standards. His work focuses on building a strong foundation for sustainable innovation and future growth.',
      skills: ['Technical Architecture', 'Core Development', 'Platform Security', 'Industry Standards', 'Innovation'],
      experience: 'Lead Developer and Founder',
      heroImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/19888.png',
      profileImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/6644.jpg',
      accentColor: 'from-[#24c35b] to-emerald-400',
      icon: <Code className="h-6 w-6" />,
      xUsername: '@ekkoplatform'
    },
    {
      id: 2,
      name: 'Eren',
      role: 'Head of Business',
      fullRole: 'Head of Business and Partnerships',
      description: 'Eren drives the growth of the project by identifying strategic partnership opportunities and leading business development efforts. He manages external relationships, builds collaborations, and oversees initiatives related to marketing and engagement. His focus is on expanding the project\'s reach and strengthening its presence in the broader ecosystem.',
      skills: ['Strategic Partnerships', 'Business Development', 'External Relations', 'Marketing', 'Ecosystem Growth'],
      experience: 'Head of Business and Partnerships',
      heroImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/9999.png',
      profileImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/667.jpg',
      accentColor: 'from-purple-500 to-violet-400',
      icon: <TrendingUp className="h-6 w-6" />,
      xUsername: '@buitenveld17653'
    },
    {
      id: 3,
      name: 'Crealla',
      role: 'Product Development',
      fullRole: 'Product Development',
      description: 'Crealla is responsible for helping build, improve, and refine EKKO\'s product offerings. He works closely with users and internal teams to ensure that the products align with real needs and evolving market trends. His work ensures that the user experience stays at the center of product development.',
      skills: ['Product Development', 'User Experience', 'Market Alignment', 'Product Refinement', 'Team Collaboration'],
      experience: 'Product Development',
      heroImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/1891.png',
      profileImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/669.jpg',
      accentColor: 'from-emerald-500 to-green-400',
      icon: <Rocket className="h-6 w-6" />,
      xUsername: '@creal04879927'
    },
    {
      id: 4,
      name: 'Jocke',
      role: 'Growth Analyst',
      fullRole: 'Growth Analyst',
      description: 'Jocke works closely with the business and partnerships team to identify new opportunities for expansion through data driven insights. This role focuses on analyzing marketing performance, tracking partnership impact, and researching emerging trends to support outreach strategies. By turning insights into action, the Growth Analyst helps optimize campaigns, strengthen collaboration efforts, and contribute directly to EKKO\'s growth and visibility in the market.',
      skills: ['Data Analysis', 'Marketing Performance', 'Partnership Impact', 'Trend Research', 'Campaign Optimization'],
      experience: 'Growth Analyst',
      heroImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/216.png',
      profileImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/665.jpg',
      accentColor: 'from-amber-500 to-orange-400',
      icon: <Award className="h-6 w-6" />,
      xUsername: '@joakimwesterlu2'
    },
    {
      id: 5,
      name: 'TezCrypto',
      role: 'Project Lead',
      fullRole: 'Project Lead',
      description: 'TezCrypto manages the day to day operations of the project, coordinating efforts across all teams to keep everything running smoothly. He ensures that key milestones are met on time and that the team remains aligned with the project\'s roadmap. His leadership plays a central role in maintaining focus, momentum, and execution.',
      skills: ['Project Management', 'Team Coordination', 'Milestone Management', 'Roadmap Alignment', 'Leadership'],
      experience: 'Project Lead',
      heroImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/889.png',
      profileImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/photo_2025-06-11_18-14-41.jpg',
      accentColor: 'from-blue-500 to-cyan-400',
      icon: <Target className="h-6 w-6" />,
      xUsername: '@Tez2013'
    }
  ];

  const teamStats = [
    {
      value: '5',
      label: 'TEAM MEMBERS',
      icon: <Users className="h-6 w-6" />,
      description: 'Expert professionals',
    },
    {
      value: '30+',
      label: 'YEARS EXPERIENCE',
      icon: <Clock className="h-6 w-6" />,
      description: 'Combined expertise',
    },
    {
      value: '100%',
      label: 'DEDICATION',
      icon: <Heart className="h-6 w-6" />,
      description: 'Full-time commitment',
    },
    {
      value: '24/7',
      label: 'SUPPORT',
      icon: <Shield className="h-6 w-6" />,
      description: 'Always available',
    }
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
      </div>

        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute top-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
          </div>

      {/* Large Background Text */}
      <div className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0 flex flex-col items-center justify-center">
        <div className="text-[12rem] font-black text-gray-300/20 leading-none text-center">
          EXPERT
        </div>
        <div className="text-[12rem] font-black text-gray-300/20 leading-none text-center">
          TEAM
        </div>
        <div className="text-[12rem] font-black text-gray-300/20 leading-none text-center">
          BUILDERS
        </div>
          </div>
            
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col z-10">
        {/* Top Content - Logo and Titles */}
        <div className="flex-1 flex flex-col items-center justify-center pt-24 pb-16 relative z-20">
          {/* EKKO Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={EKKO_LOGO}
              alt="EKKO Logo" 
              className="h-32 md:h-40 w-auto drop-shadow-lg"
            />
                    </div>
          
          {/* Main Title */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight text-center">
            <span className="text-gradient block -mb-2">
              EXPERT
            </span>
            <span className="text-gray-900 text-3xl md:text-4xl lg:text-5xl">
              TEAM BUILDERS
                      </span>
                    </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-8 max-w-3xl mx-auto text-center px-4">
            Meet the visionaries building the future of decentralized trading with cutting-edge technology and unmatched expertise.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex justify-center">
            <a
              href="https://t.me/ekkoplatform"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group"
            >
              <span>JOIN COMMUNITY</span>
              <Users className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        {/* Main Hero Image - Large and touching bottom */}
        <div className="relative z-10">
          <div className="flex justify-center">
            <img 
              src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/team.png"
              alt="EKKO Team"
              className="w-full max-w-4xl h-auto object-contain"
              style={{ marginBottom: '0', lineHeight: '0' }}
            />
          </div>
        </div>
      </section>

      {/* Team Stats Section - Flush with hero image */}
      <section className="relative z-10 bg-white" style={{ marginTop: '-1px' }}>
        <div className="container mx-auto max-w-7xl px-4 pt-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {teamStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="relative group">
                  {/* Architectural card with lines */}
                  <div className="relative arch-card rounded-2xl p-6 text-center transform hover:scale-105 transition-all duration-300 hover:shadow-2xl overflow-hidden">
                    {/* Corner architectural elements */}
                    <div className="absolute top-0 left-0 w-4 h-4">
                      <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b]"></div>
                      <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b]"></div>
                        </div>
                    <div className="absolute top-0 right-0 w-4 h-4">
                      <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b]"></div>
                      <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b]"></div>
                        </div>
                    <div className="absolute bottom-0 left-0 w-4 h-4">
                      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#24c35b]"></div>
                      <div className="absolute bottom-0 left-0 w-0.5 h-full bg-[#24c35b]"></div>
                        </div>
                    <div className="absolute bottom-0 right-0 w-4 h-4">
                      <div className="absolute bottom-0 right-0 w-full h-0.5 bg-[#24c35b]"></div>
                      <div className="absolute bottom-0 right-0 w-0.5 h-full bg-[#24c35b]"></div>
                  </div>
                  
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-lg">
                      {stat.icon}
                    </div>
                    <div className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-xs font-bold text-[#24c35b] uppercase tracking-wider mb-1">
                      {stat.label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {stat.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
              </div>
            </div>
      </section>

      {/* Team Members Section */}
      <section className="relative py-16 z-10">
        <div className="container mx-auto max-w-7xl px-4">

                    {/* Section Header */}
                    <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Meet The <span className="text-gradient">Visionaries</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our world-class team combines decades of experience in blockchain development, 
              financial technology, and product innovation to deliver the ultimate trading platform.
            </p>
          </div>

          {/* Team Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="group relative cursor-pointer transform hover:scale-105 transition-all duration-300"
                onMouseEnter={() => setHoveredMember(member.id)}
                onMouseLeave={() => setHoveredMember(null)}
                onClick={() => setSelectedMember(member)}
              >
                {/* Architectural card with enhanced styling */}
                <div className="relative arch-card rounded-2xl overflow-hidden h-full hover:shadow-2xl transition-all duration-300">
                  {/* Architectural corner lines */}
                  <div className="absolute top-0 left-0 w-6 h-6 z-10">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                    <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                  </div>
                  <div className="absolute top-0 right-0 w-6 h-6 z-10">
                    <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                    <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-6 h-6 z-10">
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                    <div className="absolute bottom-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                  </div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 z-10">
                    <div className="absolute bottom-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                    <div className="absolute bottom-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                  </div>

                  {/* Cross in center top */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-4 h-4 z-10">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-0.5 bg-[#24c35b]/60"></div>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-[#24c35b]/60"></div>
                        </div>
                  
                  {/* 50/50 Image Concept */}
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 flex">
                      {/* Left half - Team member profile image */}
                      <div className="w-1/2 relative overflow-hidden">
                        <img 
                          src={member.profileImage}
                          alt={`${member.name} profile`}
                          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-r ${member.accentColor} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
                        </div>
                      {/* Right half - Hero image with clip effect */}
                      <div className="w-1/2 relative overflow-hidden">
                        <img 
                          src={member.heroImage}
                          alt={`${member.name} - ${member.role}`}
                          className="w-full h-full object-cover object-center scale-110 group-hover:scale-125 transition-transform duration-500"
                          style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' }}
                        />
                        <div className={`absolute inset-0 bg-gradient-to-l ${member.accentColor} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
                      </div>
                    </div>
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                    
                    {/* Role badge with architectural styling */}
                    <div className="absolute top-4 right-4 z-20">
                      <div className={`relative w-12 h-12 rounded-full bg-gradient-to-br ${member.accentColor} flex items-center justify-center text-white shadow-lg`}>
                        {member.icon}
                        {/* Small architectural corner */}
                        <div className="absolute -top-1 -right-1 w-3 h-3">
                          <div className="absolute top-0 right-0 w-full h-0.5 bg-white/50"></div>
                          <div className="absolute top-0 right-0 w-0.5 h-full bg-white/50"></div>
                              </div>
                            </div>
                          </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 relative">
                    {/* Subtle architectural line */}
                    <div className="absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-[#24c35b]/30 to-transparent"></div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#24c35b] transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-[#24c35b] font-semibold mb-3 uppercase text-sm tracking-wider">
                      {member.role}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {member.description}
                    </p>
                    
                    {/* Skills tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {member.skills.slice(0, 3).map((skill, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium border border-gray-200"
                        >
                                  {skill}
                                </span>
                              ))}
                      {member.skills.length > 3 && (
                        <span className="px-2 py-1 bg-[#24c35b]/10 text-[#24c35b] text-xs rounded-full font-medium border border-[#24c35b]/20">
                          +{member.skills.length - 3} more
                        </span>
                      )}
                          </div>
                          
                    {/* Action */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">
                        {member.experience}
                      </span>
                      <div className="flex items-center text-[#24c35b] font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                        <span>View Profile</span>
                        <ArrowRight className="h-4 w-4 ml-1" />
                            </div>
                          </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
            
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-24 z-10">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="relative arch-card rounded-3xl p-12 text-center overflow-hidden">
            {/* Architectural corner decorations */}
            <div className="absolute top-0 left-0 w-8 h-8">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            <div className="absolute top-0 right-0 w-8 h-8">
              <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            <div className="absolute bottom-0 left-0 w-8 h-8">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute bottom-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            <div className="absolute bottom-0 right-0 w-8 h-8">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              <div className="absolute bottom-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
            </div>

            <div className="max-w-3xl mx-auto relative z-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-xl">
                <Users className="h-8 w-8" />
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                Join The <span className="text-gradient">EKKO Revolution</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Experience the future of decentralized trading with our cutting-edge platform. 
                Built by experts, designed for traders of all levels.
              </p>
              <div className="flex justify-center">
                <a
                  href="https://t.me/ekkoplatform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group"
                >
                  <span>JOIN COMMUNITY</span>
                  <Users className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
                    </div>
                  </div>
        </div>
      </section>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative arch-card rounded-3xl p-0 max-w-7xl w-full max-h-[95vh] overflow-hidden">
            {/* Modal architectural corners */}
            <div className="absolute top-0 left-0 w-6 h-6 z-30">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b]"></div>
              <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b]"></div>
            </div>
            <div className="absolute top-0 right-0 w-6 h-6 z-30">
              <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b]"></div>
              <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b]"></div>
            </div>

            {/* Close button */}
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-6 right-6 z-40 text-gray-400 hover:text-gray-600 text-2xl font-bold bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg"
            >
              ×
            </button>

            <div className="flex min-h-[700px]">
              {/* Left side - Profile Image */}
              <div className="w-1/2 relative overflow-hidden">
                <img 
                  src={selectedMember.profileImage} 
                  alt={selectedMember.name}
                  className="w-full h-full object-cover"
                />
                {/* Gradient overlay for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>

              {/* Right side - Profile Info */}
              <div className="w-1/2 p-10 flex flex-col justify-start relative">
                {/* Header */}
                <div className="mb-8 mt-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedMember.accentColor} flex items-center justify-center text-white shadow-lg`}>
                      {selectedMember.icon}
                    </div>
                    <div>
                      <h3 className="text-5xl font-bold text-gray-900 mb-2">{selectedMember.name}</h3>
                      <p className="text-[#24c35b] font-semibold uppercase text-base tracking-wider">
                        {selectedMember.fullRole}
                      </p>
                    </div>
                  </div>
                </div>

                                {/* About */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">About</h4>
                  <p className="text-gray-600 leading-relaxed text-base">{selectedMember.description}</p>
                </div>

                {/* Core Skills */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Core Skills</h4>
                  <div className="flex flex-wrap gap-3">
                    {selectedMember.skills.map((skill, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full font-medium border border-gray-200"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Experience</h4>
                  <p className="text-gray-600 text-base">{selectedMember.experience}</p>
                </div>

                {/* X Social Link */}
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-base font-semibold text-gray-700">Follow on X:</span>
                  <a 
                    href={`https://x.com/${selectedMember.xUsername.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors text-base font-medium"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    {selectedMember.xUsername}
                  </a>
          </div>
          </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 